import React, { useMemo, useCallback, useEffect, useState, Suspense, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    generatePath,
    useParams,
    useNavigate,
    useLocation,
    useMatch,
    matchPath,
} from 'react-router-dom';
import clsx from 'clsx';

import { tunnelSelectors, userSelectors } from 'reducers';
import { tunnelErrorsSet, codeVerifierSet } from 'reducers/tunnel';
import { blockDistributorNavigation } from 'utils/helpers';
import {
    useAddressCheck,
    useAllStepIds,
    useAllTunnelStepIds,
    useCosubscriberIdCheck,
    useProClient,
    useSubscriberIdCheck,
    useTunnelPhases,
} from 'utils/hooks/tunnel';
import { RoleNames, stepIDs, paymentMethods, tunnelPaths } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TUNNEL_STEPS } from 'containers/tunnel/tunnelSteps';
import {
    ActionButton,
    ConfirmDialog,
    InfoDialog,
    Loader,
    SubscriptionTransferDialog,
} from 'components';
import {
    Box,
    Grid,
    Typography,
    LinearProgress,
    Hidden,
    DialogContentText,
    DialogActions,
    useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';
import { useSmallScreen } from 'utils/hooks/ui';

const useStyles = makeStyles(theme => ({
    selected: {
        fontWeight: 600,
    },
    progress: {
        flex: '0 1 50%',
    },
    progressBar: {
        borderRadius: 4,
        height: 6,
        border: `1px solid ${theme.palette.primary.light}`,
        background: theme.palette.common.white,
        flex: '0 1 75px',
    },
    etape: {
        textTransform: 'uppercase',
        fontSize: 13,
        color: theme.palette.grey[700],
        [theme.breakpoints.up('sm')]: {
            marginRight: 10,
        },
    },
    navLink: {
        fontSize: 13,
        color: theme.palette.grey[700],
        display: 'inline-flex',
        alignItems: 'center',
        textTransform: 'uppercase',
        cursor: 'pointer',
        lineHeight: 1,

        '&$next': {
            justifyContent: 'flex-end',
            color: theme.palette.primary.main,
        },
    },
    next: {},
    navIcon: {
        fontSize: 16,
        margin: theme.spacing(0, 1),
    },
    transfer: {
        padding: 0,
        lineHeight: 1,
    },
}));

const TunnelPhase = props => {
    const { currentPhase, lastPhaseIndex, roleName, tunnelStepMemo, lastTunnelStepMemo } = props;
    const navigate = useNavigate();
    const { pathname } = useLocation();
    let { stepId, tsId } = useParams(); // this works because this component is "under" the path that contains those params
    stepId = +stepId;
    tsId = +tsId;

    const classes = useStyles();
    const isMobile = useSmallScreen();
    const {
        constants: { appBarHeight, phaseBarHeight },
    } = useTheme();
    const stepNavRef = useRef();

    const dispatch = useDispatch();
    const { phases } = useTunnelPhases();
    const allStepIds = useAllStepIds();
    const allTunnelStepIds = useAllTunnelStepIds();

    const { id: fundId } = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const internalUser = useSelector(userSelectors.getInternalUser);
    const userSteps = useSelector(userSelectors.getUserTunnelSteps);
    const changesVerifier = useSelector(tunnelSelectors.getFormChangesVerifier);
    const submitHandler = useSelector(tunnelSelectors.getSubmitHandler);
    const validationHandler = useSelector(tunnelSelectors.getValidationHandler);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const isCreatingSubscription = !!useMatch('/create-subscription/*');
    const subscriptionAlerts = useSelector(userSelectors.getSubscriptionAlerts);

    const [confirmChangesOpen, setConfirmChangesOpen] = useState(false);
    const [submitCallback, setSubmitCallback] = useState(null);
    const [subscriptionTransferOpen, setSubscriptionTransferOpen] = useState(false);
    const hasBlockingAlertsOnCurrentStep = useMemo(
        () => !!subscriptionAlerts?.[stepId]?.length,
        [subscriptionAlerts, stepId]
    );
    const [openFinishIndirectSubscription, setOpenFinishIndirectSubscription] = useState(false);

    const handleTransferShow = useCallback(() => {
        setOpenFinishIndirectSubscription(false);
        setSubscriptionTransferOpen(true);
    }, []);

    const currentPath = useMemo(
        () => tunnelPaths.find(path => matchPath(path, pathname)),
        [pathname]
    );

    const maxAdmissionFee = useMemo(
        () => tunnelSubscription?.investmentFund?.maximumAdmissionFee || 0,
        [tunnelSubscription]
    );

    const currentPhaseSteps = useMemo(
        () => currentPhase.tunnelSteps.filter(ts => !!allStepIds.find(sId => sId === ts.step.id)),
        [currentPhase, allStepIds]
    );

    const isDocumentStep = useMemo(
        () => [stepIDs.ID_CHECK, stepIDs.SECOND_ID_CHECK, stepIDs.ADDRESS_CHECK].includes(+stepId),
        [stepId]
    );

    const isSignatureStep = useMemo(
        () =>
            [
                stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE,
                stepIDs.SUBSCRIPTION_FORM_SIGNATURE,
            ].includes(+stepId),
        [stepId]
    );

    const phoneNumberCheck = useMemo(
        () =>
            (!!internalUser?.phoneNumber && stepId === stepIDs.CONTACT_INFO) ||
            stepId !== stepIDs.CONTACT_INFO,
        [internalUser, stepId]
    );

    // this is our local step object that contains the component to be rendered
    const currentStepMemo = useMemo(
        () => TUNNEL_STEPS.find(x => x.stepId === tunnelStepMemo?.step.id),
        [tunnelStepMemo]
    );

    // the index of the step within the whole tunnel
    const tunnelStepIndex = useMemo(
        () =>
            tsId
                ? allTunnelStepIds.findIndex(tId => tId === tsId)
                : allStepIds.findIndex(sId => sId === stepId),
        [tsId, allTunnelStepIds, allStepIds, stepId]
    );

    const stepIndexWithinPhase = useMemo(
        () =>
            tsId
                ? currentPhase.tunnelSteps.findIndex(ts => ts.id === tsId)
                : currentPhase.tunnelSteps.findIndex(ts => ts.step.id === stepId),
        [currentPhase, stepId, tsId]
    );

    const currentPhaseIndex = useMemo(
        () => phases.findIndex(ph => ph.id === currentPhase.id),
        [phases, currentPhase]
    );

    const currentUserTunnelStep = useMemo(
        () =>
            isCreatingSubscription
                ? null
                : userSteps.find(
                      us =>
                          us.tunnelStep.step.id === stepId &&
                          (tsId ? us.tunnelStep.id === tsId : true)
                  ),
        [userSteps, stepId, isCreatingSubscription, tsId]
    );

    const lastAccessibleTunnelStepIndex = useMemo(
        () => allTunnelStepIds.findIndex(tId => tId === lastTunnelStepMemo?.id),
        [allTunnelStepIds, lastTunnelStepMemo?.id]
    );

    // address document needs to be saved in the DB (so it can be used in the BS doc)
    const addressCheck = useAddressCheck();

    // ID document needs to be saved in the DB (so it can be used in the BS doc)
    const subscriberIDCheck = useSubscriberIdCheck();

    // cosubscriber ID document has to be saved in the DB (to be later used in the BS doc)
    const coSubscriberIDCheck = useCosubscriberIdCheck();

    const isDistributor =
        roleName === RoleNames.PSI ||
        roleName === RoleNames.ADMIN_CIF ||
        pathname.includes('/onboarding');
    const distAccountCreated = !!tunnelSubscription && isDistributor;

    const isProClient = useProClient();
    const usesCreditTransfer = useMemo(
        () =>
            tunnelSubscription?.bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER,
        [tunnelSubscription]
    );

    const isDistTypeChoiceStepConfirmed = useMemo(
        () => isDistributor && currentPhaseIndex === 0 && !!currentUserTunnelStep,
        [currentPhaseIndex, currentUserTunnelStep, isDistributor]
    );

    const handleOpenConfirmChanges = useCallback(
        targetPath => {
            setConfirmChangesOpen(true);
            setSubmitCallback(() => () => {
                if (targetPath) {
                    navigate(targetPath);
                }
            });
        },
        [navigate]
    );

    const stepIdAfterEligibility = useMemo(() => {
        const eligibilityIndex = allStepIds.findIndex(sId => sId === stepIDs.ELIGIBILITY);
        return allStepIds[eligibilityIndex + 1];
    }, [allStepIds]);

    const nextPath = useMemo(() => {
        const currentIndex = tsId
            ? allTunnelStepIds.findIndex(tId => tId === tsId)
            : allStepIds.findIndex(sId => sId === stepId);
        const eligibilityIndex = allStepIds.findIndex(sId => sId === stepIDs.ELIGIBILITY);
        const debitAuthorizationIndex = allStepIds.findIndex(
            sId => sId === stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE
        );
        const regulatoryDocumentsStepIndex = allStepIds.findIndex(
            sId => sId === stepIDs.REGULATORY_DOCUMENTS
        );

        if (
            isProClient &&
            eligibilityIndex > -1 &&
            currentIndex >= eligibilityIndex &&
            currentIndex < regulatoryDocumentsStepIndex
        ) {
            return generatePath(currentPath, { stepId: stepIDs.REGULATORY_DOCUMENTS });
        }

        if (usesCreditTransfer && currentIndex === debitAuthorizationIndex - 1) {
            return generatePath(currentPath, { stepId: stepIDs.SUBSCRIPTION_FORM_SIGNATURE });
        }

        if (stepId) {
            const isLastStep = currentIndex === allStepIds.length - 1;
            const targetStep = isLastStep ? stepId : allStepIds[currentIndex + 1];
            const pathObject = { stepId: targetStep };
            if (+targetStep === stepIDs.CUSTOM_STEP) {
                if (tsId) {
                    // we are currently on another custom step
                    let currentTunnelStepIndex = allTunnelStepIds.findIndex(ts => ts === +tsId);
                    pathObject.tsId = allTunnelStepIds[currentTunnelStepIndex + 1];
                } else {
                    // we are currently not on a custom step
                    pathObject.tsId = allTunnelStepIds[currentIndex + 1];
                }
            }
            return generatePath(
                pathObject.tsId ? currentPath : currentPath.replace('/:tsId', ''),
                pathObject
            );
        } else {
            return generatePath(currentPath.replace('/:tsId', ''), {
                stepId: allStepIds?.[0] || stepIDs.ACCOUNT_CREATION,
            });
        }
    }, [tsId, allTunnelStepIds, allStepIds, isProClient, usesCreditTransfer, stepId, currentPath]);

    const handleNextStep = useCallback(
        (force = false) => {
            if (
                isDistributor &&
                tunnelStepIndex === 0 &&
                currentPhaseIndex === 0 &&
                !isDistTypeChoiceStepConfirmed
            ) {
                if (changesVerifier?.()) {
                    // changes were made to the current step form
                    handleOpenConfirmChanges(null);
                    return;
                }
                return;
            }

            if (!force && changesVerifier?.()) {
                // changes were made to the current step form
                if (stepId === stepIDs.ELIGIBILITY) {
                    const customPath = generatePath(currentPath, {
                        stepId: stepIdAfterEligibility,
                    });
                    handleOpenConfirmChanges(customPath);
                } else {
                    handleOpenConfirmChanges(nextPath);
                }
            } else {
                const stepInNextPath = nextPath.split('/').reverse()[0];
                const isLastAvailableStep = +stepInNextPath === stepId;
                setConfirmChangesOpen(false);
                if (tunnelSubscription?.indirectUntransferred && isLastAvailableStep) {
                    setOpenFinishIndirectSubscription(true);
                } else {
                    navigate(nextPath);
                }
            }
        },
        [
            isDistributor,
            tunnelStepIndex,
            currentPhaseIndex,
            isDistTypeChoiceStepConfirmed,
            changesVerifier,
            handleOpenConfirmChanges,
            stepId,
            currentPath,
            stepIdAfterEligibility,
            nextPath,
            tunnelSubscription,
            navigate,
        ]
    );

    const handlePreviousStep = useCallback(() => {
        setConfirmChangesOpen(false);

        const directDebitStepIndex = allStepIds.findIndex(
            sId => sId === stepIDs.DIRECT_DEBIT_AUTHORIZATION_SIGNATURE
        );

        const regulatoryDocumentsStepIndex = allStepIds.findIndex(
            sId => sId === stepIDs.REGULATORY_DOCUMENTS
        );

        const usesCreditTransfer =
            tunnelSubscription?.bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER;

        if (
            directDebitStepIndex > -1 &&
            tunnelStepIndex === directDebitStepIndex + 1 &&
            usesCreditTransfer === true
        ) {
            // skip AP step so the previous step is from the previous phase
            const previousPath = generatePath(currentPath, {
                stepId: allStepIds[directDebitStepIndex - 1],
            });

            if (changesVerifier?.()) {
                // changes were made to the current step form
                handleOpenConfirmChanges(previousPath);
                return;
            }
            navigate(previousPath);
            return;
        }

        if (tunnelStepIndex > 0) {
            let previousStepId = allStepIds[tunnelStepIndex - 1];
            let prevPathObject = { stepId: previousStepId };

            // add tunnelStepId in path object for custom tunnel steps
            if (previousStepId === stepIDs.CUSTOM_STEP) {
                if (tsId) {
                    // we are currently on a custom step, we need to rely on the tunnel step index
                    let currentTunnelStepIndex = allTunnelStepIds.findIndex(tId => tId === +tsId);
                    prevPathObject.tsId = allTunnelStepIds[currentTunnelStepIndex - 1];
                } else {
                    // we're currently on a fixed step, we need to rely on step index
                    let currentStepIndex = allStepIds.findIndex(sId => sId === stepId);
                    prevPathObject.tsId = allTunnelStepIds[currentStepIndex - 1];
                }
            }

            let previousPath = generatePath(
                prevPathObject.tsId ? currentPath : currentPath.replace('/:tsId', ''),
                prevPathObject
            );

            // skip over 35,36,37,8,38 steps if subscriber is proClient
            if (
                regulatoryDocumentsStepIndex > -1 &&
                tunnelStepIndex === regulatoryDocumentsStepIndex &&
                isProClient
            ) {
                previousPath = generatePath(
                    prevPathObject.tsId ? currentPath : currentPath.replace('/:tsId', ''),
                    { stepId: stepIDs.ELIGIBILITY }
                );
            }

            dispatch(codeVerifierSet(false));
            if (changesVerifier?.()) {
                // changes were made to the current step form
                handleOpenConfirmChanges(previousPath);
                return;
            }
            navigate(previousPath);
        }

        if (tunnelStepIndex === 0 && currentPhaseIndex > 0) {
            const previousPath = generatePath(currentPath, {
                stepId: phases[currentPhaseIndex - 1].tunnelSteps[
                    phases[currentPhaseIndex - 1].tunnelSteps.length - 1
                ].step.id,
            });
            if (changesVerifier?.()) {
                // changes were made to the current step form
                handleOpenConfirmChanges(previousPath);
                return;
            }
            navigate(previousPath);
        }

        if (
            tunnelStepIndex === 0 &&
            currentPhaseIndex === 0 &&
            isDistributor &&
            isDistTypeChoiceStepConfirmed
        ) {
            if (changesVerifier?.()) {
                // changes were made to the current step form
                handleOpenConfirmChanges(null);
                return;
            }
        }
    }, [
        allStepIds,
        tunnelSubscription,
        tunnelStepIndex,
        currentPhaseIndex,
        isDistributor,
        isDistTypeChoiceStepConfirmed,
        currentPath,
        dispatch,
        changesVerifier,
        navigate,
        handleOpenConfirmChanges,
        isProClient,
        tsId,
        allTunnelStepIds,
        stepId,
        phases,
    ]);

    const handleConfirmChanges = useCallback(() => {
        setConfirmChangesOpen(false);
        if (typeof validationHandler === 'function') {
            // the Contact Info step requires that the validation function for the form
            // be set in redux store; it is used in the submit handler of the form
            if (validationHandler()) {
                submitHandler(submitCallback)(validationHandler());
            }
        } else {
            submitHandler(submitCallback)();
        }
    }, [submitHandler, submitCallback, validationHandler]);

    const getChangesDialogText = useCallback(() => {
        if (isDocumentStep) return texts.CONFIRM_QUIT_VERIFICATION;
        if (isSignatureStep) return texts.CONFIRM_QUIT_SIGNATURE;
        return texts.SAUVEGARDER_LES_CHANGEMENTS;
    }, [isDocumentStep, isSignatureStep]);

    // effect to trigger every time the stepId changes;
    // includes navigation through the tunnel of any kind
    useEffect(() => {
        dispatch(tunnelErrorsSet([]));
    }, [stepId, dispatch]);

    useEffect(() => {
        setTimeout(() => {
            if (stepNavRef.current) {
                stepNavRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
        }, 10);
    }, [stepId]);

    // investingSum is mandatory even in indirect subscriptions
    const hasInvestingSum = useMemo(() => {
        if (
            stepId === stepIDs.ELIGIBILITY &&
            !tunnelSubscription?.investmentCharacteristics?.investingSum &&
            tunnelSubscription?.indirectUntransferred
        ) {
            return false;
        }
        return tunnelSubscription?.indirectUntransferred && nextPath !== pathname;
    }, [tunnelSubscription, nextPath, stepId, pathname]);

    const distAccountCreationOtherProps = useMemo(
        () => (isDistributor ? { isDistTypeChoiceStepConfirmed } : {}),
        [isDistributor, isDistTypeChoiceStepConfirmed]
    );

    const canShowBackButton = useMemo(
        () =>
            (currentPhaseIndex > 0 ||
                (currentPhaseIndex === 0 && tunnelStepIndex > 0) ||
                (currentPhaseIndex === 0 &&
                    tunnelStepIndex === 0 &&
                    isDistributor &&
                    isDistTypeChoiceStepConfirmed &&
                    !distAccountCreated)) &&
            !blockDistributorNavigation(internalUser),
        [
            currentPhaseIndex,
            isDistTypeChoiceStepConfirmed,
            isDistributor,
            distAccountCreated,
            internalUser,
            tunnelStepIndex,
        ]
    );

    const canShowNextButton = useMemo(() => {
        // 1. Check if the current step is accessible
        const isStepAccessible =
            currentPhaseIndex < lastPhaseIndex || // Not at the last phase yet
            hasInvestingSum || // Subscription is indirect — user can proceed
            (currentPhaseIndex === lastPhaseIndex &&
                tunnelStepIndex < lastAccessibleTunnelStepIndex); // At last phase, but not at the last step

        // 2. If distributor is at the very beginning, ensure they’ve confirmed their type
        const isInitialDistributorStepValid =
            currentPhaseIndex === 0 && tunnelStepIndex === 0 && isDistributor
                ? isDistTypeChoiceStepConfirmed // Must confirm distributor type first
                : true; // Otherwise, no restriction

        // 3. If not a distributor, all validation checks must pass
        const areValidationsPassed = isDistributor
            ? true // Distributors bypass these checks
            : !hasBlockingAlertsOnCurrentStep &&
              phoneNumberCheck &&
              addressCheck &&
              subscriberIDCheck &&
              coSubscriberIDCheck;

        // 4. Ensure we are not on the final step in the entire flow
        const isNotFinalStep = stepId !== allStepIds[allStepIds.length - 1];

        return (
            isStepAccessible &&
            isInitialDistributorStepValid &&
            areValidationsPassed &&
            isNotFinalStep
        );
    }, [
        currentPhaseIndex,
        lastPhaseIndex,
        hasInvestingSum,
        tunnelStepIndex,
        isDistributor,
        isDistTypeChoiceStepConfirmed,
        hasBlockingAlertsOnCurrentStep,
        phoneNumberCheck,
        subscriberIDCheck,
        coSubscriberIDCheck,
        stepId,
        allStepIds,
        lastAccessibleTunnelStepIndex,
        addressCheck,
    ]);

    return (
        <Box
            id="tunnel-phase"
            width="100%"
            style={
                isCreatingSubscription
                    ? {}
                    : { maxHeight: `calc(100vh - ${phaseBarHeight + appBarHeight}px)` }
            }
            overflow="auto"
        >
            <Box id="phase-nav" width="100%" paddingTop="30px" paddingX="20px" ref={stepNavRef}>
                <Grid
                    container
                    justifyContent="space-between"
                    wrap="nowrap"
                    alignItems="center"
                    className={classes.progress}
                >
                    <Grid item xs={4}>
                        <Box display="flex" alignItems="center" justifyContent="flex-start">
                            {canShowBackButton && (
                                <Typography
                                    onClick={handlePreviousStep}
                                    className={classes.navLink}
                                >
                                    <icons.ArrowLeft className={classes.navIcon} />
                                    {texts.RETOUR}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <Typography
                                variant="body2"
                                align="center"
                                className={classes.etape}
                                noWrap
                            >{`${isMobile ? '' : texts.ETAPE} ${stepIndexWithinPhase + 1} / ${
                                currentPhaseSteps.length
                            }`}</Typography>
                            <Hidden smDown>
                                <LinearProgress
                                    variant="determinate"
                                    classes={{ root: classes.progressBar }}
                                    value={((stepIndexWithinPhase + 1) / currentPhaseSteps) * 100}
                                />
                            </Hidden>
                        </Box>
                    </Grid>
                    <Grid item xs={4}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end">
                            {canShowNextButton && (
                                <Typography
                                    align="right"
                                    onClick={() => handleNextStep()}
                                    className={clsx(classes.navLink, classes.next)}
                                >
                                    {texts.SUIVANT} <icons.ArrowRight className={classes.navIcon} />
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <ConfirmDialog
                open={confirmChangesOpen}
                title={
                    isDocumentStep || isSignatureStep
                        ? texts.QUIT_WITHOUT_FINISHING
                        : texts.CONFIRM_MODIFICATIONS
                }
                handleClose={() => setConfirmChangesOpen(false)}
                handleCancel={isDocumentStep || isSignatureStep ? undefined : submitCallback}
                handleConfirm={handleConfirmChanges}
            >
                <Typography variant="body1">{getChangesDialogText()}</Typography>
            </ConfirmDialog>
            <InfoDialog
                handleClose={() => setOpenFinishIndirectSubscription(false)}
                title={texts.FINISH_INDIRECT_SUBSCRIPTION_TITLE}
                message={
                    <React.Fragment>
                        <DialogContentText>
                            {texts.FINISH_INDIRECT_SUBSCRIPTION_CONTENT_1}
                        </DialogContentText>
                        <DialogContentText>
                            {texts.FINISH_INDIRECT_SUBSCRIPTION_CONTENT_2}
                        </DialogContentText>
                        <DialogActions>
                            <ActionButton onClick={handleTransferShow} className={classes.transfer}>
                                {texts.TRANSFER_TO_SUBSCRIBER}
                            </ActionButton>
                        </DialogActions>
                    </React.Fragment>
                }
                open={openFinishIndirectSubscription}
            />
            <SubscriptionTransferDialog
                fromTunnel
                open={subscriptionTransferOpen}
                handleClose={() => setSubscriptionTransferOpen(false)}
                maxAdmissionFee={maxAdmissionFee}
                subscriptionId={tunnelSubscription?.id}
            />
            {!(
                (!!internalUser &&
                    (userSteps.length || isCreatingSubscription || hasFinishedSubscription)) ||
                !internalUser
            ) ? (
                <Loader staticOnly />
            ) : (
                !!currentStepMemo &&
                currentStepMemo.component && (
                    <Suspense fallback={<Loader staticOnly />}>
                        <currentStepMemo.component
                            key={`${stepId}-${tsId}`}
                            roleName={roleName}
                            fundId={fundId}
                            stepId={stepId}
                            tsId={tsId}
                            currentPhase={currentPhase}
                            tunnelStep={tunnelStepMemo}
                            handleNextStep={handleNextStep}
                            userStep={currentUserTunnelStep}
                            {...distAccountCreationOtherProps}
                        />
                    </Suspense>
                )
            )}
        </Box>
    );
};

TunnelPhase.propTypes = {
    currentPhase: PropTypes.object,
    lastPhaseIndex: PropTypes.number,
    roleName: PropTypes.string,
    tunnelStepMemo: PropTypes.object,
    lastTunnelStepMemo: PropTypes.object,
};

export default TunnelPhase;
